import { faLocationDot } from '@fortawesome/free-solid-svg-icons';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import tw from 'twin.macro';
import { Button } from '../../components/button';
import {
  Alert,
  Badge,
  Card,
  CardBody,
  EmptyState,
  InlineRow,
  LoadingBlock,
  Muted,
  PageContainer,
  PageHeader,
  Stack,
  Strong,
} from '../../components/ui';
import { AddOrEditLocationModal } from '../HostCarPage/AddLocationPage/components/AddOrEditLocationModal';
import { useGetMyLocationsV2Query } from '../../services/locationServices/__generated__/queries.generated';
import { useMyWorkersQuery } from '../../services/serviceService/__generated__/queries.generated';
import { serviceErrorMessage } from './serviceErrors';
import { PROVIDER_NEW_WORKER_PATH } from './servicePaths';

const Grid = styled.div`
  ${tw`grid grid-cols-1 gap-5 sm:grid-cols-2`}
`;

/** Matches the car Locations page, so a new location shows up in both. */
const PAGE = { page: 1, limit: 20 };

/**
 * The Provider's locations (spec 019 W5, D14, D24, D25). The same location
 * form and operating hours as the car hosts use, without the tax rate: jobs
 * are taxed where the customer's home or car is. A location's hours are the
 * default hours of its workers. Customers see only the city and ZIP code.
 */
export default function LocationsPage() {
  const navigate = useNavigate();
  const query = useGetMyLocationsV2Query({
    variables: { input: PAGE },
    fetchPolicy: 'cache-and-network',
  });
  const workersQuery = useMyWorkersQuery({ fetchPolicy: 'cache-first' });
  const [editing, setEditing] = useState<number | null | 'new'>(null);
  const locations = query.data?.getMyLocationsV2.items ?? [];
  const workers = workersQuery.data?.myWorkers ?? [];
  const workerCount = (locationId: number) =>
    workers.filter(w => w.car.location?.id === locationId).length;

  return (
    <PageContainer width="lg" canvas data-testid="provider-locations">
      <PageHeader
        eyebrow="Provider"
        title="Locations"
        description="Where your workers start from. Each location has its own hours, which its workers use unless you set their own."
        actions={
          <Button
            type="button"
            text="Add a location"
            onClick={() => setEditing('new')}
            dataTestId="locations-add"
          />
        }
      />
      <Stack $gap={6}>
        <Alert tone="info" title="Customers see only the city and ZIP code">
          The street address stays private. We use it to measure the distance to each job.
        </Alert>
        {query.loading && !query.data ? (
          <LoadingBlock label="Loading your locations" />
        ) : query.error && !query.data ? (
          <Alert tone="error" title="Could not load your locations">
            {serviceErrorMessage(query.error)}
          </Alert>
        ) : locations.length === 0 ? (
          <Card>
            <EmptyState
              icon={faLocationDot}
              title="No locations yet"
              description="Add the place your workers start from, like your home or office."
              action={
                <Button type="button" text="Add a location" onClick={() => setEditing('new')} />
              }
            />
          </Card>
        ) : (
          <Grid>
            {locations.map(location => {
              const count = workerCount(location.id);
              const hoursSet = (location.operatingHours ?? []).length > 0;
              return (
                <Card key={location.id} data-testid={`location-card-${location.id}`}>
                  <CardBody>
                    <Stack $gap={4}>
                      <div>
                        <Strong>{location.name}</Strong>
                        <div>
                          <Muted $size="sm" data-testid="location-area">
                            {location.city}, {location.state} {location.zipCode}
                          </Muted>
                        </div>
                      </div>
                      <InlineRow>
                        <Badge tone={hoursSet ? 'success' : 'warning'}>
                          {hoursSet ? 'Hours set' : 'No hours yet'}
                        </Badge>
                        <Badge tone="neutral">
                          {count === 1 ? '1 worker' : `${count} workers`}
                        </Badge>
                      </InlineRow>
                      <InlineRow>
                        <Button
                          type="button"
                          theme="secondary"
                          size="sm"
                          text="Edit"
                          onClick={() => setEditing(location.id)}
                        />
                        <Button
                          type="button"
                          theme="secondary"
                          size="sm"
                          text="Hours"
                          onClick={() => navigate(`/operating-hours/${location.id}`)}
                        />
                        <Button
                          type="button"
                          theme="text"
                          size="sm"
                          text="Add a worker"
                          onClick={() => navigate(PROVIDER_NEW_WORKER_PATH)}
                        />
                      </InlineRow>
                    </Stack>
                  </CardBody>
                </Card>
              );
            })}
          </Grid>
        )}
      </Stack>
      {editing !== null && (
        <AddOrEditLocationModal
          key={String(editing)}
          isOpen
          locationId={typeof editing === 'number' ? editing : undefined}
          onClose={() => setEditing(null)}
          hideTaxRate
        />
      )}
    </PageContainer>
  );
}
